import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTableNew } from "@/components/shared/DataTable";
import { Card } from "@/components/ui/Card";
import { PlatformService } from "@/services/platformService";
import Select from "@/components/ui/Select";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/Button";
import useLiveResource from "@/hooks/useLiveResource";
import DetailModal from "../components/DetailModal";

const KIND_OPTIONS = [
  { value: "", label: "All kinds" },
  { value: "alembic", label: "Alembic" },
  { value: "rls", label: "RLS" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "started", label: "Started" },
  { value: "succeeded", label: "Succeeded" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

// Uses the global /platform/migrations endpoint filtered by tenantId (E1)
// rather than the per-tenant alias (E2) — same data, one less endpoint to
// wire up, and the filters (kind/status) are only exposed on E1 anyway.
const MigrationsTab = ({ tenantId }) => {
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);
  const [silentFetch, setSilentFetch] = useState(false);

  useEffect(() => {
    if (!silentFetch) return;
    const timer = setTimeout(() => setSilentFetch(false), 200);
    return () => clearTimeout(timer);
  }, [silentFetch]);

  // Both Alembic (tenant_migration.*) and RLS (rls_apply.*) runs land in this
  // same log — a run started elsewhere (another SuperAdmin, a fleet-wide
  // batch) otherwise only shows up here on the next manual page load.
  useLiveResource({
    keys: ["tenant_migration", "rls_apply"],
    onChange: () => setSilentFetch(true),
  });

  const handleView = (row) => {
    setSelectedMigration(row);
    setDetailOpen(true);
  };

  const extraParams = useMemo(
    () => ({
      ...(tenantId && { tenantId }),
      ...(kind && { kind }),
      ...(status && { status }),
    }),
    [tenantId, kind, status],
  );

  const api = useCallback((params) => PlatformService.fetchMigrations(params), []);

  const COLUMNS = useMemo(
    () => [
      {
        key: "startedAt",
        label: "Started at",
        render: (v) => (v ? new Date(v).toLocaleString() : "—"),
      },
      { key: "tenantSlug", label: "Tenant" },
      { key: "kind", label: "Kind" },
      { key: "status", label: "Status" },
      {
        key: "fromRevision",
        label: "From",
        render: (v) => <span className="break-all whitespace-normal">{v || "—"}</span>,
      },
      {
        key: "toRevision",
        label: "To",
        render: (v) => <span className="break-all whitespace-normal">{v || "—"}</span>,
      },
      {
        key: "durationMs",
        label: "Duration",
        render: (v) => (v != null ? `${(v / 1000).toFixed(1)}s` : "—"),
      },
      { key: "triggeredBy", label: "Triggered by" },
      {
        key: "view",
        label: "Actions",
        render: (_, row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              handleView(row);
            }}
          >
            <Eye size={16} />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-2.5">
      <Card className="flex flex-wrap items-center gap-2 p-2.5">
        <div className="w-40">
          <Select value={kind} onChange={setKind} options={KIND_OPTIONS} placeholder="All kinds" />
        </div>

        <div className="w-44">
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} placeholder="All statuses" />
        </div>
      </Card>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          columns={COLUMNS}
          api={api}
          extraParams={extraParams}
          sortable={false}
          dataPath="items"
          silentFetch={silentFetch}
          paginationLabel="migrations"
        />
        <DetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          loading={false}
          title="Migration Details"
          fields={[
            {
              label: "Started At",
              value: selectedMigration?.startedAt ? new Date(selectedMigration.startedAt).toLocaleString() : "—",
            },
            {
              label: "Finished At",
              value: selectedMigration?.finishedAt ? new Date(selectedMigration.finishedAt).toLocaleString() : "—",
            },
            { label: "Tenant", value: selectedMigration?.tenantSlug },
            { label: "Kind", value: selectedMigration?.kind },
            { label: "Status", value: selectedMigration?.status },
            { label: "From Revision", value: selectedMigration?.fromRevision },
            { label: "To Revision", value: selectedMigration?.toRevision },
            {
              label: "Duration",
              value: selectedMigration?.durationMs != null ? `${(selectedMigration.durationMs / 1000).toFixed(1)}s` : "—",
            },
            { label: "Triggered By", value: selectedMigration?.triggeredBy },
            { label: "Correlation ID", value: selectedMigration?.correlationId },
            { label: "Error", value: selectedMigration?.errorText },
          ]}
          jsonSections={[{ label: "Output", value: selectedMigration?.outputText }]}
        />
      </Card>
    </div>
  );
};

export default MigrationsTab;
